import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { AppRoutes } from '@core';
import { OrIconComponent } from '@ui-components';
import { INTRO_DESIGN_FRAME_WIDTH, INTRO_SLIDES } from '../../application/config';
import { IntroSlide, IntroductionCompletionReason } from '../../application/models';
import { CompleteIntroductionCommandHandler } from '../../application/handlers';

@Component({
  selector: 'lib-introduction',
  templateUrl: './introduction.component.html',
  styleUrls: ['./introduction.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrIconComponent]
})
export class IntroductionComponent {
  private readonly completeIntroductionCommandHandler: CompleteIntroductionCommandHandler = inject(
    CompleteIntroductionCommandHandler
  );
  private readonly router: Router = inject(Router);

  readonly slides: IntroSlide[] = INTRO_SLIDES;

  readonly index: WritableSignal<number> = signal<number>(0);

  readonly currentSlide: Signal<IntroSlide> = computed<IntroSlide>(() => this.slides[this.index()]);

  readonly isLast: Signal<boolean> = computed<boolean>(() => this.index() === this.slides.length - 1);

  readonly showSkip: Signal<boolean> = computed<boolean>(() => !this.isLast());

  readonly trackOffset: Signal<string> = computed<string>(() => `translateX(-${this.index() * 100}%)`);

  imageWidthPercent(slide: IntroSlide): number {
    return (slide.imageWidth / INTRO_DESIGN_FRAME_WIDTH) * 100;
  }

  next(): void {
    if (this.isLast()) {
      void this.finish('completed');
      return;
    }

    this.index.update((value: number) => value + 1);
  }

  skip(): void {
    void this.finish('skipped');
  }

  private async finish(reason: IntroductionCompletionReason): Promise<void> {
    try {
      await this.completeIntroductionCommandHandler.complete(reason, this.index());
    } finally {
      void this.router.navigateByUrl(`/${AppRoutes.DASHBOARD}`);
    }
  }
}
